import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { mdiCalendarBlank, mdiChevronDown } from '@mdi/js';
import { useTranslation } from 'react-i18next';

import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { IOfferEventDateFragment } from 'graphql/types';
import { List } from 'common/components/List';
import { OfferEventDateListItem } from 'offer/components/OfferEventDateListItem';

const MAX_VISIBLE_UPCOMING_DATES = 3;

interface IProps {
    canAccessData: boolean;
    dates: IOfferEventDateFragment[];
    descriptionAfterSubscription?: string | null;
    offerTitle: string;
}

export const SessionsSection = ({
    canAccessData,
    dates,
    descriptionAfterSubscription,
    offerTitle,
}: IProps) => {
    const [translate] = useTranslation();
    const [showPastSessions, setShowPastSessions] = useState(false);
    const [showAllUpcomingSessions, setShowAllUpcomingSessions] =
        useState(false);

    const { pastDates, upcomingDates } = useMemo(() => {
        const now = new Date();

        return dates.reduce<{
            pastDates: IOfferEventDateFragment[];
            upcomingDates: IOfferEventDateFragment[];
        }>(
            (acc, date) => {
                if (new Date(date.endDate) < now) {
                    acc.pastDates.push(date);
                } else {
                    acc.upcomingDates.push(date);
                }

                return acc;
            },
            { pastDates: [], upcomingDates: [] }
        );
    }, [dates]);

    const hasPastDates = !!pastDates.length;
    const hasMoreUpcomingDates =
        upcomingDates.length > MAX_VISIBLE_UPCOMING_DATES;

    const visibleUpcomingDates = showAllUpcomingSessions
        ? upcomingDates
        : upcomingDates.slice(0, MAX_VISIBLE_UPCOMING_DATES);

    const visibleDates = showPastSessions
        ? [...pastDates, ...visibleUpcomingDates]
        : visibleUpcomingDates;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {translate('offerEventDetail.dates.datesTitle', {
                        count: dates.length,
                    })}
                </Typography>

                {hasPastDates && (
                    <Button
                        aria-controls="sessions-list"
                        aria-expanded={showPastSessions}
                        sx={{ p: 0 }}
                        variant="text"
                        onClick={() => setShowPastSessions(!showPastSessions)}
                    >
                        {showPastSessions
                            ? translate(
                                  'offerEventDetail.dates.hidePastSessions'
                              )
                            : translate(
                                  'offerEventDetail.dates.showPastSessions'
                              )}
                    </Button>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                {!!visibleDates.length ? (
                    <List noBorder noPadding id="sessions-list" sx={{ mt: 2 }}>
                        {visibleDates.map((date) => (
                            <OfferEventDateListItem
                                canAccessData={canAccessData}
                                date={date}
                                descriptionAfterSubscription={
                                    descriptionAfterSubscription || undefined
                                }
                                key={date.id}
                                offerTitle={offerTitle}
                                py={1.5}
                            />
                        ))}
                    </List>
                ) : (
                    <Box
                        id="sessions-list"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                            mt: 2,
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                            color: 'body.main',
                        }}
                    >
                        <Icon
                            aria-hidden="true"
                            path={mdiCalendarBlank}
                            size="3.2rem"
                        />

                        <Typography sx={{ mt: 1 }}>
                            {translate(
                                'offerEventDetail.dates.noUpcomingSessions'
                            )}
                        </Typography>
                    </Box>
                )}

                {hasMoreUpcomingDates && (
                    <Button
                        aria-controls="sessions-list"
                        aria-expanded={showAllUpcomingSessions}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 0,
                            mt: 1,
                        }}
                        variant="text"
                        onClick={() =>
                            setShowAllUpcomingSessions(!showAllUpcomingSessions)
                        }
                    >
                        <Box
                            aria-hidden="true"
                            component="span"
                            sx={{
                                display: 'flex',
                                transform: showAllUpcomingSessions
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                                transition: 'transform 0.2s ease-in-out',
                            }}
                        >
                            <Icon path={mdiChevronDown} size="2rem" />
                        </Box>
                        {showAllUpcomingSessions
                            ? translate(
                                  'offerEventDetail.dates.hideUpcomingSessions'
                              )
                            : translate(
                                  'offerEventDetail.dates.showAllUpcomingSessions'
                              )}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
